import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  BookOpen, CheckCircle, Lock, Play, ChevronDown, ChevronRight,
  BarChart2, Clock, Target, TrendingUp, Star, Layers, Award
} from 'lucide-react'
import {
  ALL_CHAPTERS, DOMAIN_LABELS, DOMAIN_EMOJIS, DOMAIN_COLORS,
  calcStats, getGradeChapters,
  type Grade, type Chapter, type Unit, type Domain,
} from '../data/syllabus'

// ── 상수 ─────────────────────────────────────────────────────────────────────
const GRADE_LABELS: Record<Grade, string> = { 1: '중학교 1학년', 2: '중학교 2학년', 3: '중학교 3학년' }
const GRADE_EMOJIS: Record<Grade, string> = { 1: '🌱', 2: '🌿', 3: '🌳' }

const STATUS_CONFIG = {
  completed:   { icon: CheckCircle, label: '완료',    color: 'text-emerald-500', bg: 'bg-emerald-500' },
  in_progress: { icon: Play,        label: '진행 중', color: 'text-blue-500',    bg: 'bg-blue-500' },
  available:   { icon: Star,        label: '시작 가능', color: 'text-orange-400', bg: 'bg-orange-400' },
  locked:      { icon: Lock,        label: '잠김',    color: 'text-slate-300',   bg: 'bg-slate-200' },
}

// ── 진행률 계산 ───────────────────────────────────────────────────────────────
function unitProgress(unit: Unit) {
  const done = unit.topics.filter(t => t.status === 'completed').length
  return { done, total: unit.topics.length, pct: Math.round((done / unit.topics.length) * 100) }
}

function chapterProgress(ch: Chapter) {
  let done = 0, total = 0
  ch.units.forEach(u => { done += u.topics.filter(t => t.status === 'completed').length; total += u.topics.length })
  return { done, total, pct: Math.round((done / total) * 100) }
}

// ── 유닛 카드 ─────────────────────────────────────────────────────────────────
function UnitCard({ unit, color }: { unit: Unit; color: typeof DOMAIN_COLORS[Domain] }) {
  const [open, setOpen] = useState(unit.isCurrent ?? false)
  const prog = unitProgress(unit)
  const StatusIcon = STATUS_CONFIG[unit.status].icon
  const navigate = useNavigate()

  const handleUnitClick = () => {
    if (unit.contentReady && unit.status !== 'locked') {
      navigate(`/unit/${unit.id}`)
    } else {
      setOpen(!open)
    }
  }

  return (
    <div className={`rounded-xl border-2 ${unit.isCurrent ? color.border + ' shadow-md' : 'border-slate-100'} bg-white overflow-hidden transition-all`}>
      {/* Unit Header */}
      <button
        onClick={handleUnitClick}
        className="w-full text-left p-4 flex items-center gap-3 hover:bg-slate-50 transition-colors"
      >
        <span className="text-2xl flex-shrink-0">{unit.emoji}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className="text-sm font-bold text-slate-800 truncate">{unit.title}</span>
            {unit.isCurrent && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-bold flex-shrink-0 animate-pulse">
                진행 중
              </span>
            )}
            {unit.contentReady && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold flex-shrink-0">
                ✅ 콘텐츠 완성
              </span>
            )}
          </div>
          <p className="text-xs text-slate-400 truncate">{unit.description}</p>
          {/* Progress mini bar */}
          <div className="mt-2 flex items-center gap-2">
            <div className="flex-1 bg-slate-100 rounded-full h-1.5">
              <div
                className={`${STATUS_CONFIG[unit.status].bg} h-1.5 rounded-full transition-all`}
                style={{ width: `${prog.pct}%` }}
              />
            </div>
            <span className="text-xs text-slate-400 flex-shrink-0">{prog.done}/{prog.total}</span>
          </div>
        </div>
        <StatusIcon size={16} className={`flex-shrink-0 ${STATUS_CONFIG[unit.status].color}`} />
        {unit.contentReady
          ? <ChevronRight size={16} className={`flex-shrink-0 font-bold ${color.accent}`} />
          : open
            ? <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />
            : <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />
        }
      </button>

      {/* Topic List */}
      {open && (
        <div className="border-t border-slate-100 divide-y divide-slate-50">
          {unit.topics.map((topic, i) => {
            const sc = STATUS_CONFIG[topic.status]
            const TIcon = sc.icon
            return (
              <div
                key={topic.id}
                className={`flex items-center gap-3 px-4 py-2.5 ${topic.status === 'in_progress' ? color.bg + ' bg-opacity-40' : topic.status === 'available' ? 'bg-orange-50/40' : ''}`}
              >
                <span className="text-xs text-slate-300 font-mono w-4 flex-shrink-0">{i + 1}</span>
                <TIcon size={13} className={`flex-shrink-0 ${sc.color}`} />
                <span className={`text-sm flex-1 ${
                  topic.status === 'locked' ? 'text-slate-300' :
                  topic.status === 'completed' ? 'text-slate-500 line-through decoration-slate-300' :
                  'text-slate-700 font-medium'
                }`}>
                  {topic.title}
                </span>
                <span className="text-xs text-slate-300 flex-shrink-0 flex items-center gap-0.5">
                  <Clock size={10} /> {topic.estimatedMin}분
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── 챕터(도메인) 섹션 ─────────────────────────────────────────────────────────
function ChapterSection({ chapter }: { chapter: Chapter }) {
  const [expanded, setExpanded] = useState(chapter.units.some(u => u.isCurrent || u.status !== 'locked'))
  const prog = chapterProgress(chapter)
  const domainLabel = DOMAIN_LABELS[chapter.domain]
  const domainEmoji = DOMAIN_EMOJIS[chapter.domain]
  const color = chapter.color

  return (
    <div className={`rounded-2xl border-2 ${color.border} ${color.bg} overflow-hidden`}>
      {/* Chapter Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left p-4 flex items-center gap-3"
      >
        <div className={`w-10 h-10 rounded-xl ${color.light} flex items-center justify-center flex-shrink-0`}>
          <span className="text-lg">{domainEmoji}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className={`text-xs font-bold ${color.accent} uppercase tracking-wide`}>{domainLabel}</span>
          </div>
          <span className="text-base font-black text-slate-800">{chapter.title}</span>
          <div className="mt-1.5 flex items-center gap-2">
            <div className="flex-1 bg-white/60 rounded-full h-2">
              <div
                className={`${color.light} h-2 rounded-full transition-all duration-700`}
                style={{ width: `${prog.pct}%` }}
              />
            </div>
            <span className={`text-xs font-bold ${color.accent}`}>{prog.pct}%</span>
            <span className="text-xs text-slate-400">({prog.done}/{prog.total} 완료)</span>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="text-right">
            <p className="text-xs text-slate-400">{chapter.units.length}개 유닛</p>
            <p className="text-xs text-slate-400">{prog.total}개 토픽</p>
          </div>
          {expanded
            ? <ChevronDown size={18} className="text-slate-400" />
            : <ChevronRight size={18} className="text-slate-400" />}
        </div>
      </button>

      {/* Units */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {chapter.units.map(unit => (
            <UnitCard key={unit.id} unit={unit} color={color} />
          ))}
        </div>
      )}
    </div>
  )
}

// ── 학년별 탭 ─────────────────────────────────────────────────────────────────
function GradeTab({ grade, active, onClick }: { grade: Grade; active: boolean; onClick: () => void }) {
  const chapters = getGradeChapters(grade)
  let done = 0, total = 0
  chapters.forEach(c => c.units.forEach(u => {
    done += u.topics.filter(t => t.status === 'completed').length
    total += u.topics.length
  }))
  const pct = total > 0 ? Math.round((done / total) * 100) : 0

  return (
    <button
      onClick={onClick}
      className={`flex-1 py-3 px-4 rounded-xl transition-all font-medium text-sm flex flex-col items-center gap-1 ${
        active
          ? 'bg-primary-600 text-white shadow-md'
          : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
      }`}
    >
      <span className="text-xl">{GRADE_EMOJIS[grade]}</span>
      <span>{GRADE_LABELS[grade]}</span>
      <div className={`w-full rounded-full h-1.5 mt-1 ${active ? 'bg-white/30' : 'bg-slate-100'}`}>
        <div
          className={`h-1.5 rounded-full ${active ? 'bg-white' : 'bg-primary-400'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={`text-xs ${active ? 'text-white/80' : 'text-slate-400'}`}>{pct}% 완료</span>
    </button>
  )
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────
export default function SyllabusPage() {
  const [activeGrade, setActiveGrade] = useState<Grade>(1)
  const [filterDomain, setFilterDomain] = useState<Domain | 'all'>('all')
  const stats = calcStats()

  const gradeChapters = getGradeChapters(activeGrade).filter(
    c => filterDomain === 'all' || c.domain === filterDomain
  )

  const domains: Array<Domain | 'all'> = ['all', 'number', 'algebra', 'function', 'geometry', 'statistics']

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* ── Header ── */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
            <Layers size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-800">중학교 수학 전 과정</h1>
            <p className="text-sm text-slate-500">중1 ~ 중3 · 5개 영역 · {stats.totalUnits}개 유닛 · {stats.totalTopics}개 토픽</p>
          </div>
        </div>
      </div>

      {/* ── 전체 진행률 배너 ── */}
      <div className="rounded-2xl bg-gradient-to-br from-primary-600 via-primary-500 to-accent-500 text-white p-6 mb-6 shadow-lg">
        <div className="flex flex-col md:flex-row gap-5 items-start">
          <div className="flex-1">
            <p className="text-primary-200 text-sm mb-1">전체 학습 진행률</p>
            <div className="flex items-baseline gap-3 mb-3">
              <span className="text-5xl font-black">{stats.pct}%</span>
              <span className="text-primary-200 text-sm">{stats.completedTopics} / {stats.totalTopics} 토픽 완료</span>
            </div>
            <div className="bg-white/20 rounded-full h-3">
              <div className="bg-white h-3 rounded-full transition-all duration-700" style={{ width: `${stats.pct}%` }} />
            </div>
            <p className="text-xs text-primary-200 mt-2">
              예상 총 학습시간 {stats.totalHours}시간 · 완료 {stats.completedHours}시간
            </p>
          </div>
          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: BookOpen, label: '총 유닛', value: stats.totalUnits, sub: `${stats.completedUnits}개 완료` },
              { icon: Target, label: '총 토픽', value: stats.totalTopics, sub: `${stats.completedTopics}개 완료` },
              { icon: Clock, label: '총 시간', value: `${stats.totalHours}h`, sub: `${stats.completedHours}h 학습됨` },
              { icon: TrendingUp, label: '진행률', value: `${stats.pct}%`, sub: '목표: 100%' },
            ].map(({ icon: Icon, label, value, sub }) => (
              <div key={label} className="bg-white/15 rounded-xl px-3 py-2.5 text-center">
                <Icon size={14} className="text-primary-200 mx-auto mb-1" />
                <p className="text-lg font-black">{value}</p>
                <p className="text-xs text-primary-200">{label}</p>
                <p className="text-xs text-white/60">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── 학년 탭 ── */}
      <div className="flex gap-3 mb-5">
        {([1, 2, 3] as Grade[]).map(g => (
          <GradeTab key={g} grade={g} active={activeGrade === g} onClick={() => setActiveGrade(g)} />
        ))}
      </div>

      {/* ── 도메인 필터 ── */}
      <div className="flex flex-wrap gap-2 mb-5">
        {domains.map(d => {
          const isAll = d === 'all'
          const label = isAll ? '전체 영역' : `${DOMAIN_EMOJIS[d]} ${DOMAIN_LABELS[d]}`
          const color = isAll ? '' : DOMAIN_COLORS[d].badge
          return (
            <button
              key={d}
              onClick={() => setFilterDomain(d)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all ${
                filterDomain === d
                  ? isAll ? 'bg-slate-700 text-white' : color + ' ring-2 ring-offset-1 ring-current'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {label}
            </button>
          )
        })}
      </div>

      {/* ── 학년 요약 카드 (미니) ── */}
      <div className="grid grid-cols-5 gap-2 mb-5">
        {(['number', 'algebra', 'function', 'geometry', 'statistics'] as Domain[]).map(domain => {
          const chs = getGradeChapters(activeGrade).filter(c => c.domain === domain)
          if (chs.length === 0) return null
          let done = 0, total = 0
          chs.forEach(c => c.units.forEach(u => {
            done += u.topics.filter(t => t.status === 'completed').length
            total += u.topics.length
          }))
          const pct = total > 0 ? Math.round((done / total) * 100) : 0
          const color = DOMAIN_COLORS[domain]
          return (
            <button
              key={domain}
              onClick={() => setFilterDomain(filterDomain === domain ? 'all' : domain)}
              className={`rounded-xl p-2.5 text-center border-2 transition-all ${
                filterDomain === domain ? color.border + ' ' + color.bg : 'border-slate-100 bg-white hover:bg-slate-50'
              }`}
            >
              <p className="text-base">{DOMAIN_EMOJIS[domain]}</p>
              <p className="text-xs font-bold text-slate-600 mt-0.5">{DOMAIN_LABELS[domain]}</p>
              <p className={`text-xs font-black mt-0.5 ${color.accent}`}>{pct}%</p>
            </button>
          )
        })}
      </div>

      {/* ── 챕터 목록 ── */}
      <div className="space-y-4">
        {gradeChapters.length === 0 ? (
          <div className="card py-12 text-center">
            <p className="text-slate-400">이 학년·영역의 챕터가 없습니다</p>
          </div>
        ) : (
          gradeChapters.map(ch => (
            <ChapterSection key={ch.id} chapter={ch} />
          ))
        )}
      </div>

      {/* ── 범례 ── */}
      <div className="mt-6 card bg-slate-50">
        <p className="text-xs font-bold text-slate-500 mb-3 flex items-center gap-1.5">
          <Award size={12} /> 상태 범례
        </p>
        <div className="flex flex-wrap gap-4">
          {(['completed', 'in_progress', 'available', 'locked'] as const).map(s => {
            const sc = STATUS_CONFIG[s]
            const Icon = sc.icon
            return (
              <div key={s} className="flex items-center gap-1.5 text-xs">
                <Icon size={13} className={sc.color} />
                <span className="text-slate-600">{sc.label}</span>
              </div>
            )
          })}
          <div className="flex items-center gap-1.5 text-xs">
            <div className="w-3 h-1.5 bg-primary-400 rounded-full" />
            <span className="text-slate-600">진행률 바</span>
          </div>
        </div>
      </div>
    </div>
  )
}
